import { STRIPE_CONFIG } from '@/constants/stripe';
import { AppchargeErrorType } from '@/types/payment';
import type {
  StripeCardElement,
  StripeCardNumberElement,
  Stripe,
  PaymentMethod,
  StripeElements,
  CreatePaymentMethodData,
} from '@stripe/stripe-js';
// `/pure` so importing this service (the shared `paymentHandler` pulls it in,
// Chargebee flows included) doesn't inject Stripe.js — see
// `utils/stripeLoader.ts`.
import { loadStripe } from '@stripe/stripe-js/pure';
import { getStripeNextActionRedirectUrl } from '@/utils/stripe';

/**
 * Maps a Stripe PSP error reason (`last_payment_error.decline_code` falling
 * back to `last_payment_error.type`) to an `AppchargeErrorType`.
 *
 * Mirrors the BE's `StripePaymentProviderHelper.mapStripeErrorMessage` so the
 * FE-side redirect polling flow surfaces the same normalized error type that
 * `finalize-payment` would have returned. Any unknown / missing reason falls
 * back to `GENERIC_DECLINE`.
 */
const STRIPE_ERROR_CODE_TO_APPCHARGE_ERROR: Record<string, AppchargeErrorType> = {
  authentication_required: AppchargeErrorType.AUTHENTICATION_REQUIRED,
  authentication_not_handled: AppchargeErrorType.AUTHENTICATION_NOT_HANDLED,
  approve_with_id: AppchargeErrorType.APPROVE_WITH_ID,
  call_issuer: AppchargeErrorType.NOT_PERMITTED,
  card_not_supported: AppchargeErrorType.CARD_NOT_SUPPORTED,
  card_velocity_exceeded: AppchargeErrorType.CARD_VELOCITY_EXCEEDED,
  currency_not_supported: AppchargeErrorType.CURRENCY_NOT_SUPPORTED,
  do_not_honor: AppchargeErrorType.NOT_PERMITTED,
  do_not_try_again: AppchargeErrorType.PROCESSING_ERROR,
  duplicate_transaction: AppchargeErrorType.DUPLICATE_TRANSACTION,
  expired_card: AppchargeErrorType.EXPIRED_CARD,
  fraudulent: AppchargeErrorType.GENERIC_DECLINE,
  generic_decline: AppchargeErrorType.GENERIC_DECLINE,
  incorrect_address: AppchargeErrorType.INCORRECT_ADDRESS,
  incorrect_cvc: AppchargeErrorType.INVALID_CVC,
  incorrect_number: AppchargeErrorType.INVALID_NUMBER,
  incorrect_pin: AppchargeErrorType.INVALID_PIN,
  incorrect_zip: AppchargeErrorType.INVALID_ZIP,
  insufficient_funds: AppchargeErrorType.INSUFFICIENT_FUNDS,
  invalid_account: AppchargeErrorType.INVALID_ACCOUNT,
  invalid_amount: AppchargeErrorType.INVALID_AMOUNT,
  invalid_cvc: AppchargeErrorType.INVALID_CVC,
  invalid_expiry_month: AppchargeErrorType.INVALID_EXPIRY_DATE,
  invalid_expiry_year: AppchargeErrorType.INVALID_EXPIRY_DATE,
  invalid_number: AppchargeErrorType.INVALID_NUMBER,
  invalid_pin: AppchargeErrorType.INVALID_PIN,
  issuer_not_available: AppchargeErrorType.PROCESSING_ERROR,
  lost_card: AppchargeErrorType.GENERIC_DECLINE,
  merchant_blacklist: AppchargeErrorType.GENERIC_DECLINE,
  new_account_information_available: AppchargeErrorType.NOT_PERMITTED,
  no_action_taken: AppchargeErrorType.PROCESSING_ERROR,
  not_permitted: AppchargeErrorType.NOT_PERMITTED,
  offline_pin_required: AppchargeErrorType.INVALID_PIN,
  online_or_offline_pin_required: AppchargeErrorType.INVALID_PIN,
  pickup_card: AppchargeErrorType.NOT_PERMITTED,
  pin_try_exceeded: AppchargeErrorType.PIN_TRY_EXCEEDED,
  processing_error: AppchargeErrorType.PROCESSING_ERROR,
  reenter_transaction: AppchargeErrorType.PROCESSING_ERROR,
  restricted_card: AppchargeErrorType.NOT_PERMITTED,
  revocation_of_all_authorizations: AppchargeErrorType.NOT_PERMITTED,
  revocation_of_authorization: AppchargeErrorType.REVOCATION_OF_AUTHORIZATION,
  security_violation: AppchargeErrorType.NOT_PERMITTED,
  service_not_allowed: AppchargeErrorType.NOT_PERMITTED,
  stolen_card: AppchargeErrorType.NOT_PERMITTED,
  stop_payment_order: AppchargeErrorType.NOT_PERMITTED,
  testmode_decline: AppchargeErrorType.NOT_PERMITTED,
  transaction_not_allowed: AppchargeErrorType.NOT_PERMITTED,
  try_again_later: AppchargeErrorType.PROCESSING_ERROR,
  withdrawal_count_limit_exceeded: AppchargeErrorType.WITHDRAWAL_COUNT_LIMIT_EXCEEDED,
};

const BLIK_ERROR_CODE_TO_APPCHARGE_ERROR: Record<string, AppchargeErrorType> = {
  payment_method_invalid_parameter: AppchargeErrorType.BLIK_INVALID_CODE,
  payment_method_provider_timeout: AppchargeErrorType.BLIK_TIMEOUT,
};

export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

class StripeServiceClass {
  private stripe: Stripe | null = null;
  private stripeExpress: Stripe | null = null;
  initialize(stripe: Stripe) {
    this.stripe = stripe;
  }

  /**
   * True iff a non-express Stripe instance has been registered. Top-level
   * boot init in `CheckoutContent` consults this to avoid clobbering an
   * instance already set by a per-form `useStripeInitialize` (which uses
   * the selected method's sub-MID pk and is the authoritative source for
   * the active form). Without the guard, the two writers race on
   * multi-MID publishers and `createPaymentMethod` ends up on the wrong
   * instance for `cardElement` → IntegrationError.
   */
  isStripeInitialized(): boolean {
    return this.stripe !== null;
  }

  initilizeStripeExpress(stripe: Stripe) {
    this.stripeExpress = stripe;
  }

  getStripeExpress(): Stripe {
    if (!this.stripeExpress) {
      throw new Error('Stripe Express is not initialized');
    }
    return this.stripeExpress;
  }

  /**
   * Picks the instance an express wallet payment must run against.
   *
   * `stripeExpress` is ONE slot, but several express forms can be mounted at
   * once (e.g. the selected-method `GooglePayStripeForm` next to the
   * `PayButtonByDevice` express row), each with its own `Stripe` object from
   * `useStripeInitialize({ isExpress: true })` — `loadStripe` returns a fresh
   * instance per call even for the same key. Whichever resolved last owns
   * the slot, so a wallet whose `<Elements>` belongs to the other instance
   * hits `IntegrationError: the 'elements' provided was created by a
   * different Stripe instance` on confirm.
   *
   * When the caller hands over the instance that owns its `elements`, use it
   * AND promote it to `stripeExpress`, so the follow-up wallet 3DS
   * (`StripeCard3DSForm` → `getStripeExpress().handleNextAction`) runs on the
   * same account the PaymentMethod was created under. Callers that don't
   * pass one keep the previous slot semantics.
   */
  private resolveExpressStripe(stripe?: Stripe): Stripe {
    if (stripe) {
      this.stripeExpress = stripe;
      return stripe;
    }
    if (!this.stripeExpress) {
      throw new Error('Stripe is not initialized');
    }
    return this.stripeExpress;
  }
  async createStripeInstance(paymentKey: string): Promise<Stripe> {
    const stripe = await loadStripe(paymentKey, STRIPE_CONFIG);
    if (!stripe) {
      throw new Error('Failed to create stripe instance');
    }
    return stripe;
  }

  getStripe(): Stripe {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }
    return this.stripe;
  }

  updateStripeInstance(stripe: Stripe) {
    this.stripe = stripe;
  }
  async createGenericBankRequiredPaymentMethod(
    params: CreatePaymentMethodData & { elements: StripeElements }
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }
    if (!params.elements) {
      throw new Error('Elements are required');
    }
    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: params.elements,
      params: {
        billing_details: params.billing_details,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createGenegicStripePaymentMethod(params: CreatePaymentMethodData): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      type: params.type,
      ...params,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async getUrlFromSecretClientIntent(clientSecret: string): Promise<string | undefined | null> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const response = await this.stripe.retrievePaymentIntent(clientSecret);
    // Alipay uses `alipay_handle_redirect` (untyped in @stripe/stripe-js); classic
    // redirect APMs use `redirect_to_url`. Without the Alipay branch the URL is
    // lost and the PI is mis-routed to the in-page ApmNextAction driver.
    return getStripeNextActionRedirectUrl(response.paymentIntent?.next_action);
  }

  async confirmBlikPayment(clientSecret: string, code: string, email?: string) {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    return this.stripe.confirmBlikPayment(clientSecret, {
      payment_method: { blik: {}, ...(email ? { billing_details: { email } } : {}) },
      payment_method_options: { blik: { code } },
    });
  }

  async createGooglePayPaymentMethod(
    elements: StripeElements,
    billingDetails?: BillingDetails,
    stripe?: Stripe
  ): Promise<PaymentMethod> {
    const { paymentMethod, error } = await this.resolveExpressStripe(stripe).createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  /**
   * Direct pay.js Google Pay flow (`checkout_stripe_direct_google_pay`):
   * converts the Stripe Token minted by Google's `gateway: 'stripe'`
   * tokenization into a card PaymentMethod for the regular finalize path.
   * Prefers the express instance (the direct GP form initializes via
   * `useStripeInitialize({ isExpress: true })` with the wallet's sub-MID pk)
   * and falls back to the base instance for single-MID setups.
   */
  async createGooglePayPaymentMethodFromToken(
    tokenId: string,
    billingDetails?: BillingDetails,
    formStripe?: Stripe
  ): Promise<PaymentMethod> {
    const stripe = formStripe ? this.resolveExpressStripe(formStripe) : (this.stripeExpress ?? this.stripe);
    if (!stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: { token: tokenId },
      billing_details: billingDetails,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createGenericPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }
  async createApplePayPaymentMethod(
    elements: StripeElements,
    billingDetails?: BillingDetails,
    stripe?: Stripe
  ): Promise<PaymentMethod> {
    const { paymentMethod, error } = await this.resolveExpressStripe(stripe).createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createLinkPaymentMethod(elements: StripeElements, billingDetails?: unknown): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements,
      params: billingDetails as Record<string, unknown>,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  // Legacy PaymentElement flow (Amazon Pay tile → StripeGenericPayForm).
  // Bound to `this.stripe`, which is the instance the `<PaymentElement>`'s
  // `<Elements>` provider is initialized against.
  async createAmazonPayPaymentMethod(
    elements: StripeElements,
    billingDetails?: BillingDetails
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  /**
   * Confirms the Amazon Pay Express Checkout Element flow client-side.
   *
   * Amazon Pay is a redirect wallet: Stripe already drove the Amazon auth
   * popup (via `<ExpressCheckoutElement onClick={...}>`) before `onConfirm`
   * fired, so at this point the elements instance holds a completed Amazon
   * session. Two-step call:
   *   1. `elements.submit()` — validates and locks the elements state so
   *      `confirmPayment` accepts the intent handoff.
   *   2. `confirmPayment({ elements, clientSecret, redirect: 'if_required' })`
   *      — Stripe mints the `amazon_pay` PaymentMethod against the session,
   *      attaches it to the BE-provided intent, and confirms. Redirect back
   *      to Amazon usually isn't required at this stage (already handled by
   *      the popup), but `if_required` keeps us safe if Stripe decides to.
   *
   * Bound to the form's own express instance (see `resolveExpressStripe`):
   * the ECE `<Elements>` provider is initialized via
   * `useStripeInitialize({ isExpress: true })`, and `confirmPayment` must run
   * on the instance that created `elements`. Using `this.stripe` here would
   * raise an `IntegrationError` on multi-MID setups where the two differ.
   *
   * Return shape matches Stripe.js's `confirmPayment`: `{ paymentIntent?, error? }`.
   * `handleStripeExpressCheckoutAmazonPay` normalizes both into a
   * `StripePaymentResponse` alongside the BE finalize envelope.
   */
  async confirmAmazonPayExpressPayment(elements: StripeElements, clientSecret: string, stripe?: Stripe) {
    const stripeForConfirm = this.resolveExpressStripe(stripe);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return { error: submitError };
    }

    return stripeForConfirm.confirmPayment({
      elements,
      clientSecret,
      // Stripe caps `return_url` at 2048 chars and validates it upfront —
      // `window.location.href` on this checkout blows the limit because the
      // URL carries a long PSP session token in its query string. Strip to
      // origin + pathname: with `redirect: 'if_required'` this URL is only
      // navigated to if Stripe decides a hard redirect is necessary (rare
      // for Amazon Pay ECE — the Amazon popup completed the shopper auth
      // before `handleConfirm` fired), and landing on the checkout entry
      // point re-hydrates state from the session/order id the checkout
      // already holds in local state.
      confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
      redirect: 'if_required',
    });
  }

  async createKlarnaPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createIdealPaymentMethod(elements: StripeElements, billingDetails?: BillingDetails): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: billingDetails,
      },
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  async createPaymentMethod(
    cardElement: StripeCardElement | StripeCardNumberElement,
    billingDetails?: BillingDetails
  ): Promise<PaymentMethod> {
    if (!this.stripe) {
      throw new Error('Stripe is not initialized');
    }

    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: billingDetails,
    });

    if (error) throw error;
    if (!paymentMethod) throw new Error('Failed to create payment method');

    return paymentMethod;
  }

  mapBlikErrorCode(errorCode?: string | null): AppchargeErrorType {
    if (errorCode && BLIK_ERROR_CODE_TO_APPCHARGE_ERROR[errorCode]) {
      return BLIK_ERROR_CODE_TO_APPCHARGE_ERROR[errorCode];
    }
    return this.mapStripeErrorCode(errorCode);
  }

  mapStripeErrorCode(pspErrorReason?: string | null): AppchargeErrorType {
    if (pspErrorReason && STRIPE_ERROR_CODE_TO_APPCHARGE_ERROR[pspErrorReason]) {
      return STRIPE_ERROR_CODE_TO_APPCHARGE_ERROR[pspErrorReason];
    }
    return AppchargeErrorType.GENERIC_DECLINE;
  }
}

export const stripeService = new StripeServiceClass();
